// ============================================
// 🔐 LOGIN PAGE - Multi-Marca
// ============================================
// Detecta o domínio e aplica o visual correto:
//   organizai.* → OrganizAI (Claymorphism, claro)
//   integrai.*  → IntegrAI  (Glassmorphism, escuro premium)
//   outros      → Legacy    (clientes existentes, layout atual)
// ============================================
import { logCadastro } from '../../utils/auditoria';
import React, { useState } from 'react';
import { LogIn, UserPlus, Mail, Lock, User, AlertCircle, Eye, EyeOff, ArrowLeft, Building2 } from 'lucide-react';
import supabase from '../../services/supabaseClient';
import { useTenant } from '../../contexts/TenantContext';
import { useTenantAuth } from '../../hooks/useTenantAuth';
import { produtoDaUrl } from '../../config/produtoDaUrl';

// ── Detecta a marca pelo domínio ─────────────────────────────────────────────
function detectarMarca() {
  const hostname = window.location.hostname;
  if (hostname.includes('organizai')) return 'organizai';
  return 'integrai';
}

// ── Temas por marca ───────────────────────────────────────────────────────────
const TEMAS = {
  organizai: {
    nome:        'OrganizAI',
    slogan:      'Organize sua empresa do jeito certo',
    logo:        '/logo-organizai.png',
    logoFallback:'/logo192.png',
    corPrimaria: '#7c3aed',
    corHover:    '#6d28d9',
    // Containers
    bg:          '#f0eaff',
    card:        '#ffffff',
    cardBorder:  '#e0d9f7',
    cardShadow:  '6px 6px 0 #c4b5fd',
    // Tabs
    tabAtiva:    { background: '#7c3aed', color: '#fff' },
    tabInativa:  { background: '#fff', color: '#9ca3af' },
    tabBorder:   '#e0d9f7',
    // Inputs
    inputBg:     '#f9f7ff',
    inputBorder: '#e0d9f7',
    inputFocus:  '#7c3aed',
    inputText:   '#1e1b4b',
    inputPlaceholder: '#9ca3af',
    // Labels
    labelColor:  '#4c1d95',
    // Botão
    btnBg:       '#7c3aed',
    btnHover:    '#6d28d9',
    btnShadow:   '4px 4px 0 #c4b5fd',
    // Texto
    titleColor:  '#1e1b4b',
    subtitleColor:'#7c3aed',
    footerColor: '#a78bfa',
    linkColor:   '#7c3aed',
    errorBg:     '#fce7f3',
    errorBorder: '#f9a8d4',
    errorText:   '#be185d',
    successBg:   '#d1fae5',
    successBorder:'#6ee7b7',
    successText: '#065f46',
  },
  // Mesma cara do login do IntegrAI Finance (finance/design-system/tokens)
  integrai: {
    nome:        'IntegrAI CRM',
    slogan:      'Atendimento, vendas e recompra num lugar só',
    logo:        '/integrai-marca.png',
    logoFallback:'/logo192.png',
    corPrimaria: '#1078b0',
    corHover:    '#0068a0',
    bg:          'linear-gradient(180deg, #f1f4f7, #f7f9fb)',
    card:        '#ffffff',
    cardBorder:  '#e5eaef',
    cardShadow:  '0 18px 44px -16px rgba(6,44,63,.24)',
    tabAtiva:    { background: '#eff8fe', color: '#0068a0' },
    tabInativa:  { background: '#ffffff', color: '#7b868f' },
    tabBorder:   '#e5eaef',
    inputBg:     '#ffffff',
    inputBorder: '#d2dae1',
    inputFocus:  '#30b0e8',
    inputText:   '#263038',
    inputPlaceholder: '#a7b3bd',
    labelColor:  '#5a6570',
    btnBg:       '#1078b0',
    btnHover:    '#0068a0',
    btnShadow:   '0 10px 24px -12px rgba(16,120,176,.55)',
    btnCarregando: '#d6eefb',
    titleColor:  '#04283c',
    subtitleColor:'#7b868f',
    footerColor: '#a7b3bd',
    linkColor:   '#1078b0',
    caixaBg:     '#f7f9fb',
    caixaBorda:  '#e5eaef',
    caixaTexto:  '#5a6570',
    errorBg:     '#fdf1f1',
    errorBorder: '#fbe0e1',
    errorText:   '#c8373c',
    successBg:   '#f0fbf6',
    successBorder:'#dcf5e9',
    successText: '#0b8a55',
  },
  legacy: null, // usa o render original
};

// ── Componente principal ──────────────────────────────────────────────────────
const LoginPage = ({ onLogin, erroInicial }) => {
  const { tenant } = useTenant();
  const { signIn, signInComEmpresa, carregando: carregandoAuth, erro: erroAuth } = useTenantAuth();

  const [modo, setModo] = useState('login');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(erroInicial || '');
  const [sucesso, setSucesso] = useState('');
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
  const [formData, setFormData] = useState({ email: '', senha: '', nome: '', empresa: '' });

  const marca = detectarMarca();
  // integrai: nome e slogan vêm do endereço (CRM ou Entregas)
  const tema  = marca === 'integrai'
    ? { ...TEMAS.integrai, nome: produtoDaUrl().nome, slogan: produtoDaUrl().slogan }
    : TEMAS[marca];

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErro('');
    setSucesso('');
  };

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro('');
    if (!formData.email || !formData.senha) { setErro('Preencha todos os campos'); return; }
    if (!validarEmail(formData.email))       { setErro('Email inválido'); return; }

    const resultado = await signIn(formData.email, formData.senha);
    if (resultado.ok) {
      onLogin(resultado.usuario);
    } else if (resultado.empresas?.length > 0) {
      setEmpresasDisponiveis(resultado.empresas);
      setModo('selecionar-empresa');
    } else {
      setErro(erroAuth || 'Erro ao fazer login');
    }
  };

  const handleSelecionarEmpresa = async (usuario, t) => {
    const resultado = await signInComEmpresa(usuario, t);
    if (resultado?.ok) { onLogin(resultado.usuario); }
    else { setErro('Erro ao entrar na empresa selecionada'); setModo('login'); }
  };

  // Cadastro = PEDIDO de acesso: sem senha e sem vínculo. O super admin aprova e a
  // pessoa recebe o e-mail para definir a senha (api/acessos.js).
  const handleCadastro = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      if (!formData.nome || !formData.email) throw new Error('Preencha nome e e-mail');
      if (!validarEmail(formData.email))     throw new Error('Email inválido');

      const res = await fetch('/api/acessos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao: 'solicitar', nome: formData.nome, email: formData.email, empresa: formData.empresa }),
      });
      const corpo = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(corpo.erro || 'Erro ao enviar o pedido');

      await logCadastro(formData.email);
      setSucesso('Pedido enviado! Quando for aprovado, você recebe um e-mail para criar sua senha.');
      setFormData({ email: '', senha: '', nome: '', empresa: '' });
      setTimeout(() => { setModo('login'); setSucesso(''); }, 4000);
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  };

  const handleRecuperarSenha = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      if (!formData.email)               throw new Error('Digite seu email');
      if (!validarEmail(formData.email))  throw new Error('Email inválido');

      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;

      setSucesso('Se este e-mail estiver cadastrado, você receberá as instruções em breve.');
      setTimeout(() => { setModo('login'); setSucesso(''); }, 4000);
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER LEGADO — clientes existentes (sem alteração)
  // ════════════════════════════════════════════════════════════════════════════
  if (marca === 'legacy') {
    return (
      <LegacyLoginPage
        tenant={tenant}
        modo={modo} setModo={setModo}
        setErro={setErro} setSucesso={setSucesso}
        erro={erro} sucesso={sucesso}
        formData={formData} handleChange={handleChange}
        handleLogin={handleLogin} handleCadastro={handleCadastro}
        handleRecuperarSenha={handleRecuperarSenha}
        carregando={carregando}
        mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha}
        empresasDisponiveis={empresasDisponiveis}
        handleSelecionarEmpresa={handleSelecionarEmpresa}
        onVoltarEmpresa={() => { setModo('login'); setEmpresasDisponiveis([]); }}
        carregandoAuth={carregandoAuth}
      />
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER MULTI-MARCA — OrganizAI e IntegrAI
  // ════════════════════════════════════════════════════════════════════════════
  const isOrganiz = marca === 'organizai';
  const formProps = {
    formData, handleChange, handleLogin, handleCadastro,
    handleRecuperarSenha, handleSelecionarEmpresa,
    carregando, mostrarSenha, setMostrarSenha,
    tema, isOrganiz, setModo,
    empresasDisponiveis, carregandoAuth,
    onVoltar: () => { setModo('login'); setEmpresasDisponiveis([]); },
  };

  return (
    <div style={{ minHeight:'100vh', background:tema.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 16px', position:'relative', overflow:'hidden', fontFamily:'"Plus Jakarta Sans", system-ui, sans-serif' }}>
      <MultimarcaBlobs isOrganiz={isOrganiz} />
      <div style={{ width:'100%', maxWidth:420, position:'relative', zIndex:10 }}>
        <MultimarcaLogoTitulo isOrganiz={isOrganiz} tema={tema} />
        <MultimarcaCard isOrganiz={isOrganiz} tema={tema}>
          <MultimarcaTabs
            modo={modo} tema={tema} isOrganiz={isOrganiz}
            onTabChange={(id) => { setModo(id); setErro(''); setSucesso(''); }}
          />
          <MultimarcaConteudoPrincipal
            modo={modo} erro={erro} sucesso={sucesso} tema={tema}
            onVoltarLogin={() => setModo('login')}
            formProps={formProps}
          />
        </MultimarcaCard>
        <div style={{ textAlign:'center', marginTop:20 }}>
          <p style={{ fontSize:12, color:tema.footerColor, margin:0 }}>
            {tema.nome} © {new Date().getFullYear()} — Todos os direitos reservados
          </p>
          <p style={{ fontSize:12, margin:'6px 0 0' }}>
            <a href="/politica-de-privacidade" style={{ color:tema.linkColor, textDecoration:'none' }}>Política de Privacidade</a>
            <span style={{ color:tema.footerColor }}> · </span>
            <a href="/termos-de-servico" style={{ color:tema.linkColor, textDecoration:'none' }}>Termos de Serviço</a>
          </p>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Componentes auxiliares de input ──────────────────────────────────────────
const MarcaInput = ({ label, name, type, value, onChange, placeholder, Icon, tema, autoComplete }) => (
  <div>
    <label style={{ display:'block', fontSize:12.5, fontWeight:600, color:tema.labelColor, marginBottom:6 }}>{label}</label>
    <div style={{ position:'relative' }}>
      <Icon size={16} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:tema.inputPlaceholder }} />
      <input
        type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete}
        style={{
          width:'100%', boxSizing:'border-box',
          paddingLeft:38, paddingRight:14, paddingTop:11, paddingBottom:11,
          background:tema.inputBg, border:`1.5px solid ${tema.inputBorder}`,
          borderRadius:10, fontSize:13.5, color:tema.inputText,
          outline:'none', transition:'border-color 0.18s',
        }}
        onFocus={e => e.target.style.borderColor = tema.inputFocus}
        onBlur={e  => e.target.style.borderColor = tema.inputBorder}
      />
    </div>
  </div>
);

const MarcaSenhaInput = ({ label, name, value, onChange, placeholder, mostrar, setMostrar, tema, autoComplete }) => (
  <div>
    <label style={{ display:'block', fontSize:12.5, fontWeight:600, color:tema.labelColor, marginBottom:6 }}>{label}</label>
    <div style={{ position:'relative' }}>
      <Lock size={16} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:tema.inputPlaceholder }} />
      <input
        type={mostrar ? 'text' : 'password'} name={name} value={value}
        onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
        style={{
          width:'100%', boxSizing:'border-box',
          paddingLeft:38, paddingRight:40, paddingTop:11, paddingBottom:11,
          background:tema.inputBg, border:`1.5px solid ${tema.inputBorder}`,
          borderRadius:10, fontSize:13.5, color:tema.inputText,
          outline:'none', transition:'border-color 0.18s',
        }}
        onFocus={e => e.target.style.borderColor = tema.inputFocus}
        onBlur={e  => e.target.style.borderColor = tema.inputBorder}
      />
      <button type="button" onClick={() => setMostrar(!mostrar)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:tema.inputPlaceholder, display:'flex', alignItems:'center' }}>
        {mostrar ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const MarcaButton = ({ children, type, loading, tema, isOrganiz }) => (
  <button
    type={type}
    disabled={loading}
    style={{
      width:'100%', padding:'12px 0', borderRadius:12,
      background: loading ? (isOrganiz ? '#e0d9f7' : tema.btnCarregando) : tema.btnBg,
      border:'none', color:'#fff', fontSize:14, fontWeight:700,
      cursor: loading ? 'not-allowed' : 'pointer',
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      boxShadow: loading ? 'none' : tema.btnShadow,
      transition:'all 0.18s',
      marginTop:4,
    }}
  >
    {loading
      ? <div style={{ width:18, height:18, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'spin 0.7s linear infinite' }} />
      : children
    }
  </button>
);

// ── Formulários legados (extraídos para evitar repetição) ─────────────────────
const LegacyLoginForm = ({ formData, handleChange, handleLogin, carregando, mostrarSenha, setMostrarSenha, setModo }) => (
  <form onSubmit={handleLogin} className="space-y-5">
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" placeholder="seu@email.com" autoComplete="email" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Senha</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type={mostrarSenha ? 'text' : 'password'} name="senha" value={formData.senha} onChange={handleChange} className="w-full pl-11 pr-12 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" placeholder="••••••••" autoComplete="current-password" />
        <button type="button" onClick={() => setMostrarSenha(!mostrarSenha)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
          {mostrarSenha ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
    <button type="button" onClick={() => setModo('recuperar')} className="text-sm text-indigo-400 hover:text-indigo-300">Esqueceu a senha?</button>
    <button type="submit" disabled={carregando} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2">
      {carregando ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><LogIn className="w-5 h-5" />Entrar</>}
    </button>
  </form>
);

const LegacyCadastroForm = ({ formData, handleChange, handleCadastro, carregando }) => (
  <form onSubmit={handleCadastro} className="space-y-5">
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Nome Completo</label>
      <div className="relative">
        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type="text" name="nome" value={formData.nome} onChange={handleChange} className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" placeholder="Seu nome" autoComplete="name" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" placeholder="seu@email.com" autoComplete="email" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Empresa</label>
      <div className="relative">
        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type="text" name="empresa" value={formData.empresa || ''} onChange={handleChange} className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" placeholder="Onde você trabalha" autoComplete="organization" />
      </div>
    </div>
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
      <p className="text-xs text-slate-400">ℹ️ Seu pedido vai para aprovação. Aprovado, você recebe um e-mail para criar sua senha.</p>
    </div>
    <button type="submit" disabled={carregando} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2">
      {carregando ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><UserPlus className="w-5 h-5" />Pedir acesso</>}
    </button>
  </form>
);

const LegacyRecuperarForm = ({ formData, handleChange, handleRecuperarSenha, carregando }) => (
  <form onSubmit={handleRecuperarSenha} className="space-y-5">
    <div className="mb-6">
      <h3 className="text-xl font-semibold text-white mb-2">Recuperar Senha</h3>
      <p className="text-sm text-slate-400">Digite seu email e enviaremos instruções para redefinir sua senha.</p>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" placeholder="seu@email.com" autoComplete="email" />
      </div>
    </div>
    <button type="submit" disabled={carregando} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2">
      {carregando ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><Mail className="w-5 h-5" />Enviar Instruções</>}
    </button>
  </form>
);

const SeletorEmpresa = ({ empresas, onSelecionar, onVoltar, carregandoAuth }) => (
  <div className="space-y-4">
    <div className="mb-6">
      <button onClick={onVoltar} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-4 text-sm"><ArrowLeft className="w-4 h-4" />Voltar</button>
      <h3 className="text-lg font-semibold text-white mb-1">Selecione a empresa</h3>
      <p className="text-sm text-slate-400">Seu acesso está vinculado a mais de uma empresa.</p>
    </div>
    {empresas.map(({ usuario: u, tenant: t }) => (
      <button key={t.id} onClick={() => onSelecionar(u, t)} disabled={carregandoAuth}
        className="w-full flex items-center gap-4 p-4 bg-slate-900 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 rounded-xl transition-all duration-200 text-left group disabled:opacity-50">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: t.cor_primaria || '#4f46e5' }}>
          {t.logo_url ? <img src={t.logo_url} alt={t.nome} className="w-full h-full object-cover" /> : <span className="text-white font-bold text-lg">{t.nome?.charAt(0)?.toUpperCase() || '?'}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{t.nome}</p>
          <p className="text-slate-500 text-xs capitalize">{t.plano || 'padrão'}</p>
        </div>
        <LogIn className="w-4 h-4 text-slate-600 group-hover:text-indigo-400 transition-colors flex-shrink-0" />
      </button>
    ))}
    {carregandoAuth && <div className="flex justify-center pt-2"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div></div>}
  </div>
);

// ── Helpers visuais multi-marca ───────────────────────────────────────────────

const MultimarcaBlobs = ({ isOrganiz }) => (
  <>
    {isOrganiz ? (
      <>
        <div style={{ position:'absolute', width:300, height:300, borderRadius:'50%', background:'rgba(167,139,250,0.15)', filter:'blur(60px)', top:-80, right:'5%', pointerEvents:'none' }} />
        <div style={{ position:'absolute', width:200, height:200, borderRadius:'50%', background:'rgba(196,181,253,0.2)', filter:'blur(50px)', bottom:60, left:'8%', pointerEvents:'none' }} />
      </>
    ) : null
    }
  </>
);

const MultimarcaLogoIcone = ({ isOrganiz }) => isOrganiz ? (
  <div style={{ width:80, height:80, borderRadius:24, background:'linear-gradient(135deg, #7c3aed, #a855f7)', boxShadow:'6px 6px 0 #c4b5fd, inset 0 -4px 8px rgba(0,0,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:38 }}>🧩</div>
) : (
  <img src="/integrai-marca.png" alt="IntegrAI" style={{ width:120, height:'auto', display:'block' }} />
);

const MultimarcaLogoTitulo = ({ isOrganiz, tema }) => (
  <div style={{ textAlign:'center', marginBottom:28 }}>
    <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}>
      <div style={{ position:'relative' }}>
        <MultimarcaLogoIcone isOrganiz={isOrganiz} />
      </div>
    </div>
    <h1 style={{ color:tema.titleColor, fontWeight:isOrganiz ? 800 : 500, fontSize:isOrganiz ? 24 : 28, marginBottom:4, letterSpacing:'-0.02em' }}>
      {isOrganiz ? (
        <><span style={{ color:'#4c1d95' }}>Organiz</span><span style={{ color:'#7c3aed' }}>AI</span></>
      ) : (
        <><span style={{ color:'#005b8c' }}>IntegrAI</span><span style={{ color:'#30b0e8' }}>{produtoDaUrl().sufixo}</span></>
      )}
    </h1>
    <p style={{ color:tema.subtitleColor, fontSize:13 }}>{tema.slogan}</p>
  </div>
);

const MultimarcaCard = ({ isOrganiz, tema, children }) => (
  <div style={{
    background: tema.card,
    backdropFilter: 'none',
    borderRadius: isOrganiz ? 24 : 20,
    border: `1px solid ${tema.cardBorder}`,
    boxShadow: tema.cardShadow,
    overflow: 'hidden',
  }}>
    {children}
  </div>
);

const MultimarcaTabs = ({ modo, tema, isOrganiz, onTabChange }) => {
  if (modo === 'recuperar' || modo === 'selecionar-empresa') return null;
  return (
    <div style={{ display:'flex', borderBottom:`1px solid ${tema.tabBorder}` }}>
      {[{ id:'login', label:'Entrar', Icon:LogIn }, { id:'cadastro', label:'Cadastrar', Icon:UserPlus }].map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => onTabChange(id)}
          style={{
            flex:1, padding:'14px 0', fontSize:13, fontWeight:600,
            cursor:'pointer', border:'none', transition:'all 0.18s',
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
            ...(modo === id ? tema.tabAtiva : tema.tabInativa),
            ...(isOrganiz && modo === id ? { boxShadow:'inset 0 -3px 0 #7c3aed' } : {}),
          }}
        >
          <Icon size={14} />{label}
        </button>
      ))}
    </div>
  );
};

const MultimarcaAlerts = ({ erro, sucesso, tema }) => (
  <>
    {erro && (
      <div style={{ marginBottom:20, padding:'12px 14px', background:tema.errorBg, border:`1px solid ${tema.errorBorder}`, borderRadius:10, display:'flex', alignItems:'flex-start', gap:10 }}>
        <AlertCircle size={16} style={{ color:tema.errorText, flexShrink:0, marginTop:1 }} />
        <p style={{ fontSize:13, color:tema.errorText, margin:0 }}>{erro}</p>
      </div>
    )}
    {sucesso && (
      <div style={{ marginBottom:20, padding:'12px 14px', background:tema.successBg, border:`1px solid ${tema.successBorder}`, borderRadius:10, display:'flex', alignItems:'flex-start', gap:10 }}>
        <AlertCircle size={16} style={{ color:tema.successText, flexShrink:0, marginTop:1 }} />
        <p style={{ fontSize:13, color:tema.successText, margin:0 }}>{sucesso}</p>
      </div>
    )}
  </>
);

// ── Formulários multi-marca ───────────────────────────────────────────────────

const MultimarcaFormLogin = ({ formData, handleChange, handleLogin, carregando, mostrarSenha, setMostrarSenha, tema, isOrganiz, setModo }) => (
  <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:16 }}>
    <MarcaInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="seu@email.com" Icon={Mail} tema={tema} autoComplete="email" />
    <MarcaSenhaInput label="Senha" name="senha" value={formData.senha} onChange={handleChange} placeholder="••••••••" mostrar={mostrarSenha} setMostrar={setMostrarSenha} tema={tema} autoComplete="current-password" />
    <button type="button" onClick={() => setModo('recuperar')} style={{ fontSize:12, color:tema.linkColor, textAlign:'left', background:'none', border:'none', cursor:'pointer', padding:0 }}>
      Esqueceu a senha?
    </button>
    <MarcaButton type="submit" loading={carregando} tema={tema} isOrganiz={isOrganiz}>
      <LogIn size={16} />Entrar
    </MarcaButton>
  </form>
);

const MultimarcaInfoBoxCadastro = ({ isOrganiz }) => (
  <div style={{ padding:'10px 12px', background:isOrganiz ? '#f5f0ff' : '#f7f9fb', border:`1px solid ${isOrganiz ? '#e0d9f7' : '#e5eaef'}`, borderRadius:10 }}>
    <p style={{ fontSize:12, color:isOrganiz ? '#7c3aed' : '#5a6570', margin:0 }}>ℹ️ Seu pedido vai para aprovação. Aprovado, você recebe um e-mail para criar sua senha.</p>
  </div>
);

const MultimarcaFormCadastro = ({ formData, handleChange, handleCadastro, carregando, tema, isOrganiz }) => (
  <form onSubmit={handleCadastro} style={{ display:'flex', flexDirection:'column', gap:16 }}>
    <MarcaInput label="Nome Completo" name="nome" type="text" value={formData.nome} onChange={handleChange} placeholder="Seu nome" Icon={User} tema={tema} autoComplete="name" />
    <MarcaInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="seu@email.com" Icon={Mail} tema={tema} autoComplete="email" />
    <MarcaInput label="Empresa" name="empresa" type="text" value={formData.empresa || ''} onChange={handleChange} placeholder="Onde você trabalha" Icon={Building2} tema={tema} autoComplete="organization" />
    <MultimarcaInfoBoxCadastro isOrganiz={isOrganiz} />
    <MarcaButton type="submit" loading={carregando} tema={tema} isOrganiz={isOrganiz}>
      <UserPlus size={16} />Pedir acesso
    </MarcaButton>
  </form>
);

const MultimarcaItemEmpresa = ({ u, t, tema, isOrganiz, onSelecionar, carregandoAuth }) => (
  <button key={t.id} onClick={() => onSelecionar(u, t)} disabled={carregandoAuth} style={{
    display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
    background: isOrganiz ? '#f9f7ff' : tema.caixaBg,
    border: `1px solid ${isOrganiz ? '#e0d9f7' : tema.caixaBorda}`,
    borderRadius:14, cursor:'pointer', textAlign:'left', transition:'all 0.18s',
  }}>
    <div style={{ width:40, height:40, borderRadius:12, background:t.cor_primaria || tema.corPrimaria, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
      {t.logo_url
        ? <img src={t.logo_url} alt={t.nome} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : <span style={{ color:'#fff', fontWeight:700, fontSize:16 }}>{t.nome?.charAt(0)?.toUpperCase() || '?'}</span>
      }
    </div>
    <div style={{ flex:1, minWidth:0 }}>
      <p style={{ fontSize:13, fontWeight:600, color:tema.titleColor, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.nome}</p>
      <p style={{ fontSize:11, color:tema.subtitleColor, margin:0, textTransform:'capitalize' }}>{t.plano || 'padrão'}</p>
    </div>
    <LogIn size={14} style={{ color:tema.linkColor, flexShrink:0 }} />
  </button>
);

const MultimarcaFormSeletorEmpresa = ({ empresasDisponiveis, handleSelecionarEmpresa, tema, isOrganiz, carregandoAuth, onVoltar }) => (
  <div>
    <button onClick={onVoltar} style={{ display:'flex', alignItems:'center', gap:6, color:tema.linkColor, marginBottom:16, fontSize:13, cursor:'pointer', background:'none', border:'none' }}>
      <ArrowLeft size={14} />Voltar
    </button>
    <h3 style={{ fontSize:16, fontWeight:700, color:tema.titleColor, marginBottom:4 }}>Selecione a empresa</h3>
    <p style={{ fontSize:13, color:tema.subtitleColor, marginBottom:16 }}>Seu acesso está vinculado a mais de uma empresa.</p>
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {empresasDisponiveis.map(({ usuario: u, tenant: t }) => (
        <MultimarcaItemEmpresa key={t.id} u={u} t={t} tema={tema} isOrganiz={isOrganiz} onSelecionar={handleSelecionarEmpresa} carregandoAuth={carregandoAuth} />
      ))}
    </div>
    {carregandoAuth && (
      <div style={{ display:'flex', justifyContent:'center', marginTop:12 }}>
        <div style={{ width:22, height:22, borderRadius:'50%', border:`2px solid ${tema.corPrimaria}`, borderTopColor:'transparent', animation:'spin 0.7s linear infinite' }} />
      </div>
    )}
  </div>
);

const MultimarcaFormRecuperar = ({ formData, handleChange, handleRecuperarSenha, carregando, tema, isOrganiz }) => (
  <form onSubmit={handleRecuperarSenha} style={{ display:'flex', flexDirection:'column', gap:16 }}>
    <div style={{ marginBottom:4 }}>
      <h3 style={{ fontSize:16, fontWeight:700, color:tema.titleColor, margin:'0 0 6px' }}>Recuperar Senha</h3>
      <p style={{ fontSize:13, color:tema.subtitleColor, margin:0 }}>Digite seu email e enviaremos instruções para redefinir sua senha.</p>
    </div>
    <MarcaInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="seu@email.com" Icon={Mail} tema={tema} autoComplete="email" />
    <MarcaButton type="submit" loading={carregando} tema={tema} isOrganiz={isOrganiz}>
      <Mail size={16} />Enviar Instruções
    </MarcaButton>
  </form>
);

// Mapa modo → formulário: elimina 4 condicionais &&  do LoginPage
const FORMULARIOS_MULTIMARCA = {
  login:                (p) => <MultimarcaFormLogin {...p} />,
  cadastro:             (p) => <MultimarcaFormCadastro {...p} />,
  'selecionar-empresa': (p) => <MultimarcaFormSeletorEmpresa {...p} />,
  recuperar:            (p) => <MultimarcaFormRecuperar {...p} />,
};

const MultimarcaConteudoPrincipal = ({ modo, erro, sucesso, tema, onVoltarLogin, formProps }) => (
  <div style={{ padding:'28px 28px' }}>
    {modo === 'recuperar' && (
      <button onClick={onVoltarLogin} style={{ display:'flex', alignItems:'center', gap:6, color:tema.linkColor, marginBottom:20, fontSize:13, cursor:'pointer', background:'none', border:'none' }}>
        <ArrowLeft size={14} />Voltar para login
      </button>
    )}
    <MultimarcaAlerts erro={erro} sucesso={sucesso} tema={tema} />
    {FORMULARIOS_MULTIMARCA[modo]?.(formProps)}
  </div>
);

// ── Helpers do render legado ──────────────────────────────────────────────────

// Complexidade: 4 (3 operadores de fallback/ternário + base)
const LegacyLogoSection = ({ tenant }) => (
  <div className="text-center mb-8">
    <div className="flex justify-center mb-4">
      <div className="relative">
        <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full"></div>
        <img
          src={tenant?.logo_url || '/logo-integrai.jpeg'}
          onError={(e) => { e.target.onerror = null; e.target.src = '/logo192.png'; }}
          alt={tenant?.nome ? `${tenant.nome} Logo` : 'IntegrAI Logo'}
          className="relative w-32 h-32 object-contain drop-shadow-2xl"
          style={{ filter: 'brightness(1.1) contrast(1.2) drop-shadow(0 0 20px rgba(99,102,241,0.4))' }}
        />
      </div>
    </div>
    <h1 className="text-white font-bold text-xl mb-1">{tenant?.nome || 'IntegrAI'}</h1>
    <p className="text-slate-400">Sistema de Gestão Integrado</p>
  </div>
);

// Complexidade: 5 (if + || + 2 ternários de className + base)
const LegacyTabNav = ({ modo, setModo, setErro, setSucesso }) => {
  if (modo === 'recuperar' || modo === 'selecionar-empresa') return null;
  return (
    <div className="flex border-b border-slate-700">
      <button onClick={() => { setModo('login'); setErro(''); setSucesso(''); }}
        className={`flex-1 py-4 text-sm font-semibold transition-all ${modo === 'login' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}>
        <LogIn className="inline-block w-4 h-4 mr-2" />Entrar
      </button>
      <button onClick={() => { setModo('cadastro'); setErro(''); setSucesso(''); }}
        className={`flex-1 py-4 text-sm font-semibold transition-all ${modo === 'cadastro' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}>
        <UserPlus className="inline-block w-4 h-4 mr-2" />Cadastrar
      </button>
    </div>
  );
};

// Complexidade: 3 (2 &&  de alerta + base)
const LegacyAlerts = ({ erro, sucesso }) => (
  <>
    {erro    && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" /><p className="text-sm text-red-300">{erro}</p></div>}
    {sucesso && <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-3"><AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" /><p className="text-sm text-green-300">{sucesso}</p></div>}
  </>
);

// Complexidade: 6 (5 &&  de modo + base)
const LegacyFormRenderer = ({ modo, setModo, formData, handleChange, handleLogin, handleCadastro, handleRecuperarSenha, carregando, mostrarSenha, setMostrarSenha, empresasDisponiveis, handleSelecionarEmpresa, onVoltarEmpresa, carregandoAuth }) => (
  <>
    {modo === 'recuperar' && (
      <button onClick={() => setModo('login')} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 text-sm">
        <ArrowLeft className="w-4 h-4" />Voltar para login
      </button>
    )}
    {modo === 'login'              && <LegacyLoginForm formData={formData} handleChange={handleChange} handleLogin={handleLogin} carregando={carregando} mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha} setModo={setModo} />}
    {modo === 'cadastro'           && <LegacyCadastroForm formData={formData} handleChange={handleChange} handleCadastro={handleCadastro} carregando={carregando} mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha} />}
    {modo === 'selecionar-empresa' && <SeletorEmpresa empresas={empresasDisponiveis} onSelecionar={handleSelecionarEmpresa} onVoltar={onVoltarEmpresa} carregandoAuth={carregandoAuth} />}
    {modo === 'recuperar'          && <LegacyRecuperarForm formData={formData} handleChange={handleChange} handleRecuperarSenha={handleRecuperarSenha} carregando={carregando} />}
  </>
);

// ── Render legado — Complexidade: 1 (sem galhos) ──────────────────────────────
const LegacyLoginPage = ({ tenant, modo, setModo, setErro, setSucesso, erro, sucesso, formData, handleChange, handleLogin, handleCadastro, handleRecuperarSenha, carregando, mostrarSenha, setMostrarSenha, empresasDisponiveis, handleSelecionarEmpresa, onVoltarEmpresa, carregandoAuth }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
    <div className="w-full max-w-md">
      <LegacyLogoSection tenant={tenant} />
      <div className="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        <LegacyTabNav modo={modo} setModo={setModo} setErro={setErro} setSucesso={setSucesso} />
        <div className="p-8">
          <LegacyAlerts erro={erro} sucesso={sucesso} />
          <LegacyFormRenderer
            modo={modo} setModo={setModo} formData={formData} handleChange={handleChange}
            handleLogin={handleLogin} handleCadastro={handleCadastro} handleRecuperarSenha={handleRecuperarSenha}
            carregando={carregando} mostrarSenha={mostrarSenha} setMostrarSenha={setMostrarSenha}
            empresasDisponiveis={empresasDisponiveis} handleSelecionarEmpresa={handleSelecionarEmpresa}
            onVoltarEmpresa={onVoltarEmpresa} carregandoAuth={carregandoAuth}
          />
        </div>
      </div>
      <div className="text-center mt-6">
        <p className="text-sm text-slate-500">IntegrAI © 2025 - Todos os direitos reservados</p>
        <p className="text-sm mt-1">
          <a href="/politica-de-privacidade" className="text-slate-500 hover:text-slate-300">Política de Privacidade</a>
          <span className="text-slate-600"> · </span>
          <a href="/termos-de-servico" className="text-slate-500 hover:text-slate-300">Termos de Serviço</a>
        </p>
      </div>
    </div>
  </div>
);

export default LoginPage;
